const _jsxFileName = '';
import React, { useState, useEffect } from 'react';
import {
  Building,
  Save,
  Plus,
  Trash2,
  Edit,
  X,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Loader,
} from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL;

// ✅ FIXED: Use the exact same key as in your update
const ADMIN_KEY = 'sparkling-gems-admin-2026';

const AdminBankDetails = ({ onNavigate, user }) => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    bank_name: '',
    branch: '',
    account_number: '',
    account_name: 'Sparkling Gems Inc.',
    currency: 'PHP',
    swift_code: '',
    routing_number: '',
    instructions: '',
    is_active: true,
    is_default: false,
  });

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/bank-details/get`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setBanks(data.banks || []);
      } else {
        setError(data.error || 'Failed to fetch banks');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? e.target.checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      const payload = editingBank ? { ...formData, id: editingBank.id } : formData;

      const endpoint = editingBank ? '/bank-details/update' : '/bank-details/save';
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(
          editingBank ? 'Bank details updated successfully' : 'Bank details added successfully'
        );
        setShowForm(false);
        setEditingBank(null);
        resetForm();
        fetchBanks();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to save bank details');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (bank) => {
    setEditingBank(bank);
    setFormData(bank);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this bank account?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/bank-details/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify({ id }),
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Bank deleted successfully');
        fetchBanks();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to delete bank');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    }
  };

  const handleToggleActive = async (bank) => {
    try {
      const updatedBank = { ...bank, is_active: !bank.is_active };
      const response = await fetch(`${API_BASE_URL}/bank-details/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify(updatedBank),
      });
      const data = await response.json();
      if (data.success) {
        fetchBanks();
      } else {
        setError(data.error || 'Failed to update bank');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      bank_name: '',
      branch: '',
      account_number: '',
      account_name: 'Sparkling Gems Inc.',
      currency: 'PHP',
      swift_code: '',
      routing_number: '',
      instructions: '',
      is_active: true,
      is_default: false,
    });
  };

  const handleAddNew = () => {
    setEditingBank(null);
    resetForm();
    setShowForm(true);
  };

  if (loading) {
    return React.createElement(
      'div',
      {
        className: 'pt-44 pb-12 min-h-screen bg-stone-50',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 210 },
      },
      React.createElement(
        'div',
        {
          className: 'max-w-7xl mx-auto px-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 211 },
        },
        React.createElement(
          'div',
          {
            className: 'flex justify-center items-center h-64',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 212 },
          },
          React.createElement(
            'div',
            {
              className: 'text-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 213 },
            },
            React.createElement(Loader, {
              size: 32,
              className: 'animate-spin text-gold-600 mx-auto mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 214 },
            }),
            React.createElement(
              'p',
              {
                className: 'text-stone-500',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 215 },
              },
              'Loading bank accounts...'
            )
          )
        )
      )
    );
  }

  return React.createElement(
    'div',
    {
      className: 'pt-44 pb-12 min-h-screen bg-stone-50',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 224 },
    },
    React.createElement(
      'div',
      {
        className: 'max-w-7xl mx-auto px-6',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 225 },
      },

      /* Header with back button */
      React.createElement(
        'div',
        {
          className: 'flex justify-between items-center mb-8',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 228 },
        },
        React.createElement(
          'div',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 229 } },
          (user?.role === 'admin' || (user?.permissions || []).includes('view_dashboard')) &&
            React.createElement(
              'button',
              {
                onClick: () => onNavigate('admin-dashboard'),
                className:
                  'flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 230 },
              },

              React.createElement(ArrowLeft, {
                size: 14,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 234 },
              }),
              ' Back to Dashboard'
            ),
          React.createElement(
            'h1',
            {
              className: 'font-serif text-3xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 236 },
            },
            'Bank Account Management'
          ),
          React.createElement(
            'p',
            {
              className: 'text-stone-500 font-light mt-2 text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 237 },
            },
            'Manage bank transfer details displayed at checkout'
          )
        ),

        React.createElement(
          'button',
          {
            onClick: handleAddNew,
            className:
              'px-6 py-3 bg-gold-600 text-white rounded-sm hover:bg-gold-700 flex items-center gap-2 shadow-lg',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 240 },
          },

          React.createElement(Plus, {
            size: 16,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 244 },
          }),
          ' Add New Bank Account'
        )
      ),

      /* Status Messages */
      error &&
        React.createElement(
          'div',
          {
            className:
              'mb-6 p-4 bg-red-50 border border-red-200 rounded-sm flex items-center gap-2 text-red-700',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 250 },
          },
          React.createElement(AlertCircle, {
            size: 16,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 251 },
          }),
          ' ',
          error,
          React.createElement(
            'button',
            {
              onClick: () => setError(null),
              className: 'ml-auto text-red-500 hover:text-red-700',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 252 },
            },
            React.createElement(X, {
              size: 16,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 253 },
            })
          )
        ),

      success &&
        React.createElement(
          'div',
          {
            className:
              'mb-6 p-4 bg-green-50 border border-green-200 rounded-sm flex items-center gap-2 text-green-700',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 259 },
          },
          React.createElement(CheckCircle, {
            size: 16,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 260 },
          }),
          ' ',
          success,
          React.createElement(
            'button',
            {
              onClick: () => setSuccess(null),
              className: 'ml-auto text-green-500 hover:text-green-700',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 261 },
            },
            React.createElement(X, {
              size: 16,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 262 },
            })
          )
        ),

      /* Banks Table */
      React.createElement(
        'div',
        {
          className: 'bg-white border border-stone-200 rounded-sm overflow-hidden shadow-lg',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 268 },
        },
        banks.length === 0
          ? React.createElement(
              'div',
              {
                className: 'p-12 text-center text-stone-400',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 270 },
              },
              React.createElement(Building, {
                size: 48,
                className: 'mx-auto mb-4 text-stone-300',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 271 },
              }),
              React.createElement(
                'p',
                {
                  className: 'text-lg mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 272 },
                },
                'No bank accounts added yet'
              ),
              React.createElement(
                'p',
                {
                  className: 'text-sm mb-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 273 },
                },
                'Add your first bank account to display at checkout'
              ),
              React.createElement(
                'button',
                {
                  onClick: handleAddNew,
                  className: 'text-gold-600 hover:text-gold-500 text-sm underline',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 274 },
                },
                'Add Bank Account'
              )
            )
          : React.createElement(
              'table',
              {
                className: 'w-full',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 282 },
              },
              React.createElement(
                'thead',
                {
                  className: 'bg-stone-100',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 283 },
                },
                React.createElement(
                  'tr',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 284 } },
                  React.createElement(
                    'th',
                    {
                      className: 'px-6 py-4 text-left text-xs uppercase text-stone-500 font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 285 },
                    },
                    'Bank'
                  ),
                  React.createElement(
                    'th',
                    {
                      className: 'px-6 py-4 text-left text-xs uppercase text-stone-500 font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 286 },
                    },
                    'Account Details'
                  ),
                  React.createElement(
                    'th',
                    {
                      className: 'px-6 py-4 text-left text-xs uppercase text-stone-500 font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 287 },
                    },
                    'Currency'
                  ),
                  React.createElement(
                    'th',
                    {
                      className: 'px-6 py-4 text-left text-xs uppercase text-stone-500 font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 288 },
                    },
                    'Status'
                  ),
                  React.createElement(
                    'th',
                    {
                      className: 'px-6 py-4 text-right text-xs uppercase text-stone-500 font-bold',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 289 },
                    },
                    'Actions'
                  )
                )
              ),
              React.createElement(
                'tbody',
                {
                  className: 'divide-y divide-stone-100',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 292 },
                },
                banks.map((bank) =>
                  React.createElement(
                    'tr',
                    {
                      key: bank.id,
                      className: 'hover:bg-stone-50 transition-colors',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 294 },
                    },
                    React.createElement(
                      'td',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 295 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'font-medium text-stone-900',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 296 },
                        },
                        bank.bank_name
                      ),
                      bank.branch &&
                        React.createElement(
                          'div',
                          {
                            className: 'text-xs text-stone-500',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 298 },
                          },
                          bank.branch
                        )
                    ),
                    React.createElement(
                      'td',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 301 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'font-mono text-sm',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 302 },
                        },
                        bank.account_number
                      ),
                      React.createElement(
                        'div',
                        {
                          className: 'text-xs text-stone-500',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 303 },
                        },
                        bank.account_name
                      ),
                      (bank.swift_code || bank.routing_number) &&
                        React.createElement(
                          'div',
                          {
                            className: 'text-[10px] text-stone-400 mt-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 305 },
                          },
                          bank.swift_code && `SWIFT: ${bank.swift_code}`,
                          bank.swift_code && bank.routing_number && ' • ',
                          bank.routing_number && `Routing: ${bank.routing_number}`
                        )
                    ),
                    React.createElement(
                      'td',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 312 },
                      },
                      React.createElement(
                        'span',
                        {
                          className: 'font-medium',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 313 },
                        },
                        bank.currency
                      )
                    ),
                    React.createElement(
                      'td',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 315 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'flex flex-col gap-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 316 },
                        },
                        bank.is_default &&
                          React.createElement(
                            'span',
                            {
                              className:
                                'inline-block px-2 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full w-fit',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 318 },
                            },
                            'Default'
                          ),
                        React.createElement(
                          'button',
                          {
                            onClick: () => handleToggleActive(bank),
                            className: `inline-block px-2 py-0.5 text-[10px] font-bold rounded-full w-fit cursor-pointer ${
                              bank.is_active
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                            }`,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 322 },
                          },

                          bank.is_active ? 'Active' : 'Inactive'
                        )
                      )
                    ),
                    React.createElement(
                      'td',
                      {
                        className: 'px-6 py-4 text-right',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 334 },
                      },
                      React.createElement(
                        'button',
                        {
                          onClick: () => handleEdit(bank),
                          className: 'text-gold-600 hover:text-gold-700 mr-3 p-1',
                          title: 'Edit',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 335 },
                        },

                        React.createElement(Edit, {
                          size: 16,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 340 },
                        })
                      ),
                      React.createElement(
                        'button',
                        {
                          onClick: () => bank.id && handleDelete(bank.id),
                          className: 'text-red-600 hover:text-red-700 p-1',
                          title: 'Delete',
                          disabled: bank.is_default,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 342 },
                        },

                        React.createElement(Trash2, {
                          size: 16,
                          className: bank.is_default ? 'opacity-30' : '',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 348 },
                        })
                      )
                    )
                  )
                )
              )
            )
      ),

      /* Add/Edit Form Modal */
      showForm &&
        React.createElement(
          'div',
          {
            className: 'fixed inset-0 bg-black/50 flex items-center justify-center z-50',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 360 },
          },
          React.createElement(
            'div',
            {
              className: 'bg-white p-8 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 361 },
            },
            React.createElement(
              'div',
              {
                className: 'flex justify-between items-center mb-6',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 362 },
              },
              React.createElement(
                'h2',
                {
                  className: 'font-serif text-2xl',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 363 },
                },
                editingBank ? 'Edit' : 'Add',
                ' Bank Account'
              ),
              React.createElement(
                'button',
                {
                  onClick: () => setShowForm(false),
                  className: 'text-stone-400 hover:text-stone-600',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 366 },
                },
                React.createElement(X, {
                  size: 24,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 367 },
                })
              )
            ),

            React.createElement(
              'form',
              {
                onSubmit: handleSubmit,
                className: 'space-y-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 371 },
              },
              React.createElement(
                'div',
                {
                  className: 'grid grid-cols-2 gap-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 372 },
                },
                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 373 } },
                  React.createElement(
                    'label',
                    {
                      className: 'block text-xs uppercase text-stone-500 font-bold mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 374 },
                    },
                    'Bank Name ',
                    React.createElement(
                      'span',
                      {
                        className: 'text-red-500',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 375 },
                      },
                      '*'
                    )
                  ),
                  React.createElement('input', {
                    type: 'text',
                    name: 'bank_name',
                    value: formData.bank_name,
                    onChange: handleInputChange,
                    required: true,
                    className:
                      'w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none',
                    placeholder: 'e.g., BPI - Bank of the Philippine Islands',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 377 },
                  })
                ),
                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 387 } },
                  React.createElement(
                    'label',
                    {
                      className: 'block text-xs uppercase text-stone-500 font-bold mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 388 },
                    },
                    'Branch (Optional)'
                  ),
                  React.createElement('input', {
                    type: 'text',
                    name: 'branch',
                    value: formData.branch,
                    onChange: handleInputChange,
                    className:
                      'w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none',
                    placeholder: 'e.g., Main Branch',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 391 },
                  })
                )
              ),

              React.createElement(
                'div',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 402 } },
                React.createElement(
                  'label',
                  {
                    className: 'block text-xs uppercase text-stone-500 font-bold mb-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 403 },
                  },
                  'Account Number ',
                  React.createElement(
                    'span',
                    {
                      className: 'text-red-500',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 404 },
                    },
                    '*'
                  )
                ),
                React.createElement('input', {
                  type: 'text',
                  name: 'account_number',
                  value: formData.account_number,
                  onChange: handleInputChange,
                  required: true,
                  className:
                    'w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none',
                  placeholder: 'e.g., 1234-5678-90',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 406 },
                })
              ),

              React.createElement(
                'div',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 417 } },
                React.createElement(
                  'label',
                  {
                    className: 'block text-xs uppercase text-stone-500 font-bold mb-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 418 },
                  },
                  'Account Name ',
                  React.createElement(
                    'span',
                    {
                      className: 'text-red-500',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 419 },
                    },
                    '*'
                  )
                ),
                React.createElement('input', {
                  type: 'text',
                  name: 'account_name',
                  value: formData.account_name,
                  onChange: handleInputChange,
                  required: true,
                  className:
                    'w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none',
                  placeholder: 'e.g., Sparkling Gems Inc.',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 421 },
                })
              ),

              React.createElement(
                'div',
                {
                  className: 'grid grid-cols-2 gap-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 432 },
                },
                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 433 } },
                  React.createElement(
                    'label',
                    {
                      className: 'block text-xs uppercase text-stone-500 font-bold mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 434 },
                    },
                    'Currency'
                  ),
                  React.createElement(
                    'select',
                    {
                      name: 'currency',
                      value: formData.currency,
                      onChange: handleInputChange,
                      className:
                        'w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 437 },
                    },

                    React.createElement(
                      'option',
                      {
                        value: 'PHP',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 443 },
                      },
                      'PHP - Philippine Peso'
                    ),
                    React.createElement(
                      'option',
                      {
                        value: 'USD',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 444 },
                      },
                      'USD - US Dollar'
                    ),
                    React.createElement(
                      'option',
                      {
                        value: 'EUR',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 445 },
                      },
                      'EUR - Euro'
                    )
                  )
                ),
                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 448 } },
                  React.createElement(
                    'label',
                    {
                      className: 'block text-xs uppercase text-stone-500 font-bold mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 449 },
                    },
                    'SWIFT Code'
                  ),
                  React.createElement('input', {
                    type: 'text',
                    name: 'swift_code',
                    value: formData.swift_code,
                    onChange: handleInputChange,
                    className:
                      'w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none',
                    placeholder: 'e.g., BOPIPHMM',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 452 },
                  })
                )
              ),

              React.createElement(
                'div',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 463 } },
                React.createElement(
                  'label',
                  {
                    className: 'block text-xs uppercase text-stone-500 font-bold mb-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 464 },
                  },
                  'Routing Number'
                ),
                React.createElement('input', {
                  type: 'text',
                  name: 'routing_number',
                  value: formData.routing_number,
                  onChange: handleInputChange,
                  className:
                    'w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none',
                  placeholder: 'e.g., 123456789',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 467 },
                })
              ),

              React.createElement(
                'div',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 477 } },
                React.createElement(
                  'label',
                  {
                    className: 'block text-xs uppercase text-stone-500 font-bold mb-1',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 478 },
                  },
                  'Instructions (Shown to customers)'
                ),
                React.createElement('textarea', {
                  name: 'instructions',
                  value: formData.instructions,
                  onChange: handleInputChange,
                  rows: 3,
                  className:
                    'w-full border border-stone-300 p-2 rounded-sm focus:border-gold-600 focus:outline-none',
                  placeholder:
                    'e.g., Please use your order number as reference when making the transfer.',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 481 },
                })
              ),

              React.createElement(
                'div',
                {
                  className: 'flex items-center gap-6',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 491 },
                },
                React.createElement(
                  'label',
                  {
                    className: 'flex items-center gap-2 cursor-pointer',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 492 },
                  },
                  React.createElement('input', {
                    type: 'checkbox',
                    name: 'is_active',
                    checked: formData.is_active,
                    onChange: handleInputChange,
                    className: 'w-4 h-4 accent-gold-600',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 493 },
                  }),
                  React.createElement(
                    'span',
                    {
                      className: 'text-sm',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 500 },
                    },
                    'Active'
                  )
                ),
                React.createElement(
                  'label',
                  {
                    className: 'flex items-center gap-2 cursor-pointer',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 502 },
                  },
                  React.createElement('input', {
                    type: 'checkbox',
                    name: 'is_default',
                    checked: formData.is_default,
                    onChange: handleInputChange,
                    className: 'w-4 h-4 accent-gold-600',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 503 },
                  }),
                  React.createElement(
                    'span',
                    {
                      className: 'text-sm',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 510 },
                    },
                    'Default Bank'
                  )
                )
              ),

              React.createElement(
                'div',
                {
                  className: 'flex gap-4 pt-4 border-t border-stone-200',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 514 },
                },
                React.createElement(
                  'button',
                  {
                    type: 'submit',
                    disabled: saving,
                    className:
                      'flex-1 bg-gold-600 text-white py-3 rounded-sm hover:bg-gold-700 flex items-center justify-center gap-2 disabled:opacity-50',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 515 },
                  },

                  saving
                    ? React.createElement(
                        React.Fragment,
                        null,
                        React.createElement(Loader, {
                          size: 16,
                          className: 'animate-spin',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 522 },
                        }),
                        'Saving...'
                      )
                    : React.createElement(
                        React.Fragment,
                        null,
                        React.createElement(Save, {
                          size: 16,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 527 },
                        }),
                        editingBank ? 'Update' : 'Save',
                        ' Bank Details'
                      )
                ),
                React.createElement(
                  'button',
                  {
                    type: 'button',
                    onClick: () => setShowForm(false),
                    className: 'flex-1 border border-stone-300 py-3 rounded-sm hover:bg-stone-50',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 532 },
                  },
                  'Cancel'
                )
              )
            )
          )
        )
    )
  );
};

export default AdminBankDetails;
